// Pure helpers for the file-source masking inventory view: group inventory entries
// (table = file) under their folder path, build inline-inventory subsets for per-file /
// per-folder masking jobs, and gate actions on scheme/format support.
//
// Inventory table keys for file sources are paths relative to the source rootPath —
// today always a bare file name (discovery approves only the source's own root), but
// the helpers tolerate '/', '\', and the zip pseudo-dir marker '!/' for the reserved
// future subdir shape.

/** Base file name of an inventory table key ('sub/a.csv' → 'a.csv', 'z.zip!/a.csv' → 'a.csv'). */
export function fileBaseName(tableKey) {
  const s = String(tableKey || '');
  const idx = Math.max(s.lastIndexOf('/'), s.lastIndexOf('\\'));
  return idx >= 0 ? s.slice(idx + 1) : s;
}

/** Directory part of a table key, '' when none. Keeps the '!' on a zip pseudo-dir ('z.zip!'). */
export function fileDirName(tableKey) {
  const s = String(tableKey || '');
  const idx = Math.max(s.lastIndexOf('/'), s.lastIndexOf('\\'));
  return idx >= 0 ? s.slice(0, idx) : '';
}

/** Display label for the folder header: source rootPath plus any relative dir. */
export function fileFolderOf(tableKey, rootPath) {
  const root = String(rootPath || '').replace(/\/+$/, '');
  const dir = fileDirName(tableKey);
  return dir ? `${root}/${dir}` : root || '/';
}

/** Lowercased extension of the base name, '' when none. */
export function fileExtension(tableKey) {
  const base = fileBaseName(tableKey);
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot === base.length - 1) return '';
  return base.slice(dot + 1).toLowerCase();
}

/**
 * Extensions maskable for a given file DatasourceType — mirrors
 * Drivers/fileFormatSupport.requiredFormatsForFileType (Document/Excel/Generic → null:
 * not maskable regardless of advertised capability).
 */
export function maskableExtensionsForType(datasourceType) {
  switch (datasourceType) {
    case 'FILE_DELIMITED_FOLDER':
      return ['csv', 'tsv'];
    case 'FILE_PARQUET_FOLDER':
      return ['parquet'];
    case 'FILE_JSON_FOLDER':
      return ['json', 'jsonl', 'ndjson'];
    case 'FILE_XML_FOLDER':
      return ['xml'];
    default:
      return null;
  }
}

/**
 * Content-sniffed sampler types (DatasourceType names from discovery's per-file
 * tables[].sampler_type) that masking can process. FILE_DOCUMENT_FOLDER is the
 * "couldn't sniff a structured format" bucket — not maskable. FILE_FIXED_WIDTH_FOLDER
 * masks via the masking service's FIXED_WIDTH parser; the column layout discovery
 * inferred rides the inventory tableMeta (fixedWidthColumns/recordLength). Note the
 * masking writer TRUNCATES a masked value longer than its column — prefer
 * length-preserving algorithms (FPE) for fixed-width fields.
 */
const MASKABLE_SNIFFED_TYPES = new Set([
  'FILE_DELIMITED_FOLDER',
  'FILE_PARQUET_FOLDER',
  'FILE_JSON_FOLDER',
  'FILE_XML_FOLDER',
  'FILE_FIXED_WIDTH_FOLDER',
]);

/**
 * True when this file is maskable. The content-sniffed format from the discovery
 * results (`sniffedType`) is authoritative when present — files are masked by what
 * they contain, not what they're named, so extensionless files in a generic folder
 * mask fine when they sniffed as delimited/JSON/XML/Parquet. Without a sniffed type
 * (runs older than the sampler_type stamp), fall back to the extension heuristic
 * against the source's declared type.
 */
export function isFileMaskable(tableKey, datasourceType, sniffedType) {
  if (sniffedType) return MASKABLE_SNIFFED_TYPES.has(sniffedType);
  const exts = maskableExtensionsForType(datasourceType);
  if (exts == null) return false;
  const ext = fileExtension(tableKey);
  // Extensionless keys inside a typed folder inherit the folder's format claim.
  return ext === '' || exts.includes(ext);
}

/**
 * Drop inventory entries (table = file) whose format masking doesn't support, so they
 * are never persisted into a saved inventory. `tableMeta` (tableKey → {sniffedType,
 * delimiter}, from discoverTablesToFileTableMeta) supplies the per-file sniffed format;
 * entries without meta fall back to the extension heuristic. Only meaningful for file
 * sources — callers must gate on isFileDatasourceType (for non-file types every
 * meta-less key would be dropped).
 */
export function dropUnmaskableFiles(selectedFields, datasourceType, tableMeta) {
  return Object.fromEntries(
    Object.entries(selectedFields || {}).filter(([tableKey]) =>
      isFileMaskable(tableKey, datasourceType, tableMeta?.[tableKey]?.sniffedType)
    )
  );
}

/** Count of fields staged for masking (checked + non-blank algorithm) in one table's map. */
export function selectedFieldCount(fieldsMap) {
  let n = 0;
  Object.keys(fieldsMap || {}).forEach((field) => {
    const fd = fieldsMap[field] || {};
    const algo = fd.algorithm != null ? String(
      typeof fd.algorithm === 'object' ? fd.algorithm.algorithm || '' : fd.algorithm
    ).trim() : '';
    if (fd.checked && algo) n += 1;
  });
  return n;
}

/**
 * Group the selectedFields tables (table = file) by their folder for display:
 * [{ folder, files: [{ tableKey, baseName, ext, sniffedType, selectedCount, totalCount,
 * maskable }] }], folders and files sorted ascending. `tableMeta` supplies each file's
 * content-sniffed format (see dropUnmaskableFiles) for the maskable flag and chip.
 */
export function groupFilesByFolder(selectedFields, rootPath, datasourceType, tableMeta) {
  const byFolder = new Map();
  Object.keys(selectedFields || {}).forEach((tableKey) => {
    const folder = fileFolderOf(tableKey, rootPath);
    if (!byFolder.has(folder)) byFolder.set(folder, []);
    const sniffedType = tableMeta?.[tableKey]?.sniffedType;
    byFolder.get(folder).push({
      tableKey,
      baseName: fileBaseName(tableKey),
      ext: fileExtension(tableKey),
      sniffedType,
      selectedCount: selectedFieldCount(selectedFields[tableKey]),
      totalCount: Object.keys(selectedFields[tableKey] || {}).length,
      maskable: isFileMaskable(tableKey, datasourceType, sniffedType),
    });
  });
  return [...byFolder.entries()]
    .map(([folder, files]) => ({
      folder,
      files: files.sort((a, b) => a.baseName.localeCompare(b.baseName)),
    }))
    .sort((a, b) => a.folder.localeCompare(b.folder));
}

/**
 * Inline inventory covering only `tableKeys`, in the top-level tables[] form the
 * platform normalizer passes through untouched (no schema key → tableName survives
 * verbatim, sidestepping the dot-split hazard for multi-dot file names). Only fields
 * with checked + a non-blank algorithm are included; tables left with no such field
 * are dropped (the normalizer would drop them anyway and an empty files[] 400s at
 * the worker). Returns null when nothing survives — caller must not start a job.
 */
export function buildInlineFileInventory(selectedFields, tableKeys, tableMeta) {
  const tables = [];
  (tableKeys || []).forEach((tableKey) => {
    const fieldsMap = (selectedFields || {})[tableKey];
    if (!fieldsMap) return;
    const columns = [];
    Object.keys(fieldsMap).forEach((field) => {
      const fd = fieldsMap[field] || {};
      if (!fd.checked) return;
      const algoRaw = fd.algorithm;
      if (algoRaw == null) return;
      if (typeof algoRaw === 'object' && algoRaw.algorithm != null) {
        columns.push({
          name: field,
          type: fd.dataType ? String(fd.dataType).toUpperCase() : 'VARCHAR',
          sensitive: true,
          algorithm: algoRaw,
        });
        return;
      }
      const algo = String(algoRaw).trim();
      if (!algo) return;
      columns.push({
        name: field,
        type: fd.dataType ? String(fd.dataType).toUpperCase() : 'VARCHAR',
        sensitive: true,
        algorithm: algo,
      });
    });
    if (columns.length > 0) {
      // Per-file routing metadata (sniffedType/delimiter from discovery) rides the
      // table object; the platform retains these keys for format resolution.
      const meta = tableMeta && tableMeta[tableKey] ? tableMeta[tableKey] : null;
      tables.push({ name: tableKey, columns, ...(meta || {}) });
    }
  });
  if (tables.length === 0) return null;
  return { tables };
}

/**
 * Whether this source's storage scheme supports file masking today. Mirrors the
 * platform's buildFileMaskingLocation (LOCAL + SFTP only; S3/Azure → 400) and the
 * worker's lack of zip-internal masking.
 */
export function fileSchemeSupport(source) {
  const scheme = (source && source.vfsScheme) || 'LOCAL';
  if (String(source && source.rootPath ? source.rootPath : '').includes('!/')) {
    return { enabled: false, reason: 'Masking inside zip archives is not yet supported.' };
  }
  if (scheme === 'LOCAL' || scheme === 'SFTP') {
    return { enabled: true, reason: null };
  }
  return { enabled: false, reason: 'File masking currently supports Local and SFTP sources only.' };
}
